import React, { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Calendar, DayOfWeek, List, PrimaryButton, Stack } from "@fluentui/react";
import { DropDownPicker, TimePicker, css, dateTimeUtils, useTheme } from "cayo.ui";
import { IDropdownOption } from "@cayo/shared/api/types/schema";
import logger from "@cayo/shared/libs/logger";
import { commonMessages } from "@cayo/shared/messages/common";
import PastPeriod, { PastPeriodDataType, pastPeriodMap } from "./components/pastPeriod";
import { onRenderCell } from "./renderers";
import { BlankSpace, IntervalWrapper, calendarDayProps, classNames } from "./styles";
import { IDateTimeRange } from "../../api/schema.api";
import { dateRangeConverters } from "../../scheme/converters/date-range-converters";
import { useDateTime } from "../DateTimeBox/hooks";
import DatePickerStrings from "../DateTimeBox/pickerStrings";
import { IFormComponent } from "../QueryFilterForm/IFormComponent";

type Nullable<T> = T | null;

const log = logger.getLogger("InplaceDateTimeRange");

const InplaceDateTimeRange: FC<
  IDateTimeRange & IFormComponent & { pastPeriodData: Nullable<PastPeriodDataType> }
> = (props) => {
  const intl = useIntl();
  const { cayoTheme } = useTheme();
  const { brandColors } = cayoTheme;

  const [isCustomInterval, setIsCustomInterval] = useState(true);
  const [isPastPeriod, setIsPastPeriod] = useState(true);
  const [pastPeriodNumber, setPastPeriodNumber] = useState(props.pastPeriodData?.number || 1);
  const [pastPeriodType, setPastPeriodType] = useState(
    props.pastPeriodData?.type || "lastWeeksInterval"
  );

  useEffect(() => {
    setIsCustomInterval(props.predefinedRange === "Custom");
    setIsPastPeriod(props.predefinedRange === "PastPeriod");
  }, [props.predefinedRange]);

  useEffect(() => {
    if (props.pastPeriodData) {
      setPastPeriodNumber(props.pastPeriodData?.number || 1);
      setPastPeriodType(props.pastPeriodData?.type || "lastWeeksInterval");
    }
  }, [props.pastPeriodData]);

  const intervalItems = useMemo<IDropdownOption[] | undefined>(() => {
    const result = props.predefinedRanges!.map<IDropdownOption>((r) => ({
      key: r.value!,
      text: r.name!,
      value: r.value!,
      selected: isCustomInterval
        ? r.value === "Custom"
        : isPastPeriod
          ? r.value === "PastPeriod"
          : r.value === props.predefinedRange,
    }));
    log.debug("props.predefinedRanges, intervalItems -> ", props.predefinedRanges, result);
    return result;
  }, [props.predefinedRange, isCustomInterval, isPastPeriod]);

  const {
    handleDateChanged: handleBeginDateChanged,
    handleTimeChanged: handleBeginTimeChanged,
    interval: beginInterval,
    setInterval: setBeginInterval,
    time: beginTime,
    date: beginDate,
    timeError: beginTimeError,
  } = useDateTime({
    ...props,
    value: props.begin,
  });

  const {
    handleDateChanged: handleEndDateChanged,
    handleTimeChanged: handleEndTimeChanged,
    interval: endInterval,
    setInterval: setEndInterval,
    time: endTime,
    date: endDate,
    timeError: endTimeError,
  } = useDateTime({
    ...props,
    value: props.end,
  });

  const fullDate = useMemo(
    () =>
      dateTimeUtils.getDisplayDate(beginInterval || props.begin) +
      " - " +
      dateTimeUtils.getDisplayDate(endInterval || props.end),
    [beginInterval, endInterval, props.begin, props.end]
  );

  const title = useMemo(() => {
    if (props.predefinedRange === "Custom") {
      return props.shortName || props.name;
    } else if (props.predefinedRange === "PastPeriod") {
      const postfix = pastPeriodNumber > 1 ? "s" : "";
      const period = pastPeriodMap[pastPeriodType as keyof typeof pastPeriodMap];
      return `Last ${pastPeriodNumber} ${period}${postfix}`;
    } else {
      if (props.predefinedRange === "0" && !props.label) {
        return props.shortName || props.name;
      }
      return props.predefinedRanges?.find((r) => r.value === props.predefinedRange)?.name || "";
    }
  }, [props, pastPeriodNumber, pastPeriodType]);

  const toggleDropDownPicker = useRef<() => void>();

  const handleSelect = () => {
    if (isCustomInterval) {
      props.setValue(
        dateRangeConverters.fromRange(beginInterval || props.begin, endInterval || props.end),
        props
      );
    }

    if (isPastPeriod) {
      props.setValue(dateRangeConverters.previousPeriod(pastPeriodNumber, pastPeriodType), props);
    }

    toggleDropDownPicker?.current && toggleDropDownPicker.current();
  };

  return (
    <DropDownPicker
      title={title}
      initToggle={(f) => (toggleDropDownPicker.current = f)}
      calloutPadding={0}
      disabled={props.disabled}
      label={props.label}
      onClose={() => {
        if (props.predefinedRange !== "Custom") {
          setIsCustomInterval(false);
        }
      }}
      isActive={props.predefinedRange !== "0" || !!props.begin || !!props.end}
    >
      <Stack horizontal={true} verticalAlign="stretch" verticalFill={true}>
        <List
          className={classNames.optionsList}
          items={intervalItems}
          onClick={(event) => {
            const clickedItem = props.predefinedRanges?.find(
              (r) => r.name === (event.target as any).innerText
            )?.value;
            log.debug("List.onClick -> item, clickedItem", event, clickedItem);
            if (!clickedItem) {
              return;
            }

            if (clickedItem === "Custom") {
              if (!beginInterval) {
                setBeginInterval(dateTimeUtils.getTodayBeginDate());
                setEndInterval(dateTimeUtils.getTodayEndDate());
              }
              setIsCustomInterval(true);
              setIsPastPeriod(false);
            } else if (clickedItem === "PastPeriod") {
              setBeginInterval(undefined);
              setEndInterval(undefined);
              setIsCustomInterval(false);
              setIsPastPeriod(true);
            } else {
              setIsCustomInterval(false);
              setIsPastPeriod(false);
              setBeginInterval(undefined);
              setEndInterval(undefined);
              if (log.isDebugEnabled()) {
                log.debug(
                  "List.onClick -> setValue -> clickedItem, value",
                  clickedItem,
                  dateRangeConverters.fromPredefinedRange(clickedItem)
                );
              }
              props.setValue(dateRangeConverters.fromPredefinedRange(clickedItem), props);
              toggleDropDownPicker?.current && toggleDropDownPicker.current();
            }
          }}
          onRenderCell={onRenderCell}
        ></List>

        <Stack
          horizontal={false}
          styles={{
            root: { borderLeft: `1px solid ${brandColors.divider}`, position: "relative" },
          }}
        >
          {isPastPeriod && (
            <PastPeriod
              number={pastPeriodNumber}
              type={pastPeriodType}
              onChangeNumber={setPastPeriodNumber}
              onchangeType={setPastPeriodType}
            />
          )}
          {!isPastPeriod && (
            <>
              {!isCustomInterval && <BlankSpace>&nbsp;</BlankSpace>}
              <Stack
                horizontal
                verticalAlign={"center"}
                verticalFill
                className={classNames.calendarsWrapper}
              >
                <Stack horizontal={false} horizontalAlign="center" className={classNames.calendar}>
                  <Calendar
                    onSelectDate={handleBeginDateChanged}
                    isMonthPickerVisible={false}
                    value={beginDate}
                    firstDayOfWeek={DayOfWeek.Sunday}
                    strings={DatePickerStrings()}
                    isDayPickerVisible={true}
                    highlightCurrentMonth={true}
                    highlightSelectedMonth={true}
                    showMonthPickerAsOverlay={true}
                    showGoToToday={false}
                    allFocusable={true}
                    calendarDayProps={calendarDayProps}
                  />
                  <TimePicker
                    setValue={(v) => handleBeginTimeChanged(v, true)}
                    value={beginTime}
                    defaultValue="00:00"
                  />
                </Stack>
                <Stack horizontal={false} horizontalAlign="center" className={classNames.calendar}>
                  <Calendar
                    onSelectDate={handleEndDateChanged}
                    isMonthPickerVisible={false}
                    value={endDate}
                    firstDayOfWeek={DayOfWeek.Sunday}
                    strings={DatePickerStrings()}
                    isDayPickerVisible={true}
                    highlightCurrentMonth={true}
                    highlightSelectedMonth={true}
                    showMonthPickerAsOverlay={true}
                    showGoToToday={false}
                    calendarDayProps={calendarDayProps}
                  />
                  <TimePicker
                    setValue={(v) => handleEndTimeChanged(v)}
                    value={endTime}
                    defaultValue="23:59"
                  />
                </Stack>
              </Stack>
            </>
          )}
          <IntervalWrapper justifyContent="space-between" valign>
            <div className={css.pl8}>{isCustomInterval ? fullDate : <Fragment />}</div>
            <PrimaryButton
              disabled={
                isCustomInterval &&
                (!!beginTimeError ||
                  !!endTimeError ||
                  (beginInterval && endInterval && beginInterval.getTime() > endInterval.getTime()))
              }
              text={intl.formatMessage(commonMessages.select)}
              onClick={handleSelect}
            />
          </IntervalWrapper>
        </Stack>
      </Stack>
    </DropDownPicker>
  );
};

export default InplaceDateTimeRange;
